/**
 * Theme charging sims — multi-pass WebGL2 plumbing.
 *
 * The single-pass shaders (glContext.ts + ShaderChargingAnimation)
 * render one fragment program straight to the canvas. The REAL
 * simulations (stable-fluids for Initiative / Electric Light,
 * physarum for Dark Electric) need what that layer deliberately
 * doesn't have: offscreen render targets, double-buffered ping-pong
 * textures, and float formats. This module owns exactly that and
 * nothing else — program compile/caching stays in glContext's
 * getOrCreateProgram (same per-context WeakMap, sim cache keys are
 * namespaced 'sim:...').
 *
 * Float format policy: RGBA16F render targets when
 * EXT_color_buffer_float is present (half-float is linear-filterable
 * in core WebGL2, and blending against 16F targets is allowed under
 * the same extension — the EXT_float_blend restriction only bites
 * 32F). Absent the extension (rare: very old or software GL), fall
 * back to RGBA8 — the sims still run, with visibly reduced dynamic
 * range, which beats a blank canvas.
 */

// ── Render targets ───────────────────────────────────────────────────

export interface SimTarget {
  texture: WebGLTexture;
  fbo: WebGLFramebuffer;
  width: number;
  height: number;
  /** 1/width, 1/height — the uTexelSize uniform every pass wants. */
  texelSize: readonly [number, number];
}

export interface SimDoubleTarget {
  read: SimTarget;
  write: SimTarget;
  swap: () => void;
  width: number;
  height: number;
  texelSize: readonly [number, number];
}

export interface SimFormats {
  /** Sized internal format for sim textures (RGBA16F or RGBA8). */
  internalFormat: number;
  /** Texel type matching the internal format. */
  type: number;
  /** True when float targets are available (EXT_color_buffer_float). */
  float: boolean;
}

/** Resolve the best renderable texture format once per context. */
export function resolveSimFormats(gl: WebGL2RenderingContext): SimFormats {
  const ext = gl.getExtension('EXT_color_buffer_float');
  if (ext) {
    return { internalFormat: gl.RGBA16F, type: gl.HALF_FLOAT, float: true };
  }
  return { internalFormat: gl.RGBA8, type: gl.UNSIGNED_BYTE, float: false };
}

/**
 * Allocate one offscreen render target. Linear filtering +
 * CLAMP_TO_EDGE: the fluid passes rely on clamped sampling as the
 * implicit boundary condition, and linear filtering is what makes
 * semi-Lagrangian advection look smooth at low sim resolutions.
 * Returns null when allocation or FBO completeness fails (context
 * loss mid-setup) — callers bail out and the host component falls
 * back exactly like a compile failure.
 */
export function createSimTarget(
  gl: WebGL2RenderingContext,
  width: number,
  height: number,
  formats: SimFormats,
  filtering: number = gl.LINEAR,
): SimTarget | null {
  const texture = gl.createTexture();
  if (!texture) return null;
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filtering);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filtering);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texStorage2D(gl.TEXTURE_2D, 1, formats.internalFormat, width, height);

  const fbo = gl.createFramebuffer();
  if (!fbo) {
    gl.deleteTexture(texture);
    return null;
  }
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  const complete = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  if (!complete) {
    // eslint-disable-next-line no-console
    console.error('[simGl] framebuffer incomplete for sim target');
    gl.deleteFramebuffer(fbo);
    gl.deleteTexture(texture);
    return null;
  }
  return {
    texture,
    fbo,
    width,
    height,
    texelSize: [1 / width, 1 / height] as const,
  };
}

/** Double-buffered target pair for ping-pong passes. */
export function createSimDoubleTarget(
  gl: WebGL2RenderingContext,
  width: number,
  height: number,
  formats: SimFormats,
  filtering: number = gl.LINEAR,
): SimDoubleTarget | null {
  const a = createSimTarget(gl, width, height, formats, filtering);
  if (!a) return null;
  const b = createSimTarget(gl, width, height, formats, filtering);
  if (!b) {
    disposeSimTarget(gl, a);
    return null;
  }
  const pair = {
    read: a,
    write: b,
    width,
    height,
    texelSize: a.texelSize,
    swap: () => {
      const t = pair.read;
      pair.read = pair.write;
      pair.write = t;
    },
  };
  return pair;
}

export function disposeSimTarget(gl: WebGL2RenderingContext, t: SimTarget): void {
  gl.deleteFramebuffer(t.fbo);
  gl.deleteTexture(t.texture);
}

export function disposeSimDoubleTarget(gl: WebGL2RenderingContext, t: SimDoubleTarget): void {
  disposeSimTarget(gl, t.read);
  disposeSimTarget(gl, t.write);
}

// ── Pass execution ───────────────────────────────────────────────────

/**
 * Bind a texture to a texture unit and return the unit index for the
 * sampler uniform. Tiny sugar that keeps the pass call sites honest
 * about which unit each sampler landed on.
 */
export function bindTexture(
  gl: WebGL2RenderingContext,
  texture: WebGLTexture,
  unit: number,
): number {
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  return unit;
}

/**
 * Run one fullscreen pass into `target` (or the canvas when null).
 * The caller has already: bound the program (gl.useProgram), set its
 * uniforms, and bound source textures to units. This helper owns the
 * FBO bind + viewport + quad draw only. The quad attribute state is
 * per-program — see setupSimQuad below.
 */
export function blitTo(
  gl: WebGL2RenderingContext,
  target: SimTarget | null,
): void {
  if (target) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
    gl.viewport(0, 0, target.width, target.height);
  } else {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
  }
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}

/**
 * Bind the shared quad buffer's aPos attribute for a program. Call
 * once per program at sim init (attribute locations are per-program;
 * the buffer itself is shared). Mirrors glContext.setupQuadBuffer's
 * contract without allocating a new buffer per program.
 */
export function setupSimQuad(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  quadBuffer: WebGLBuffer,
): void {
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  const loc = gl.getAttribLocation(program, 'aPos');
  if (loc === -1) return;
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
}

/** Shared sim vertex shader — quad positions to clip space + vUv. */
export const SIM_VERTEX_SOURCE = `#version 300 es
precision highp float;
in vec2 aPos;
out vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;
